import React, { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Eye, Pencil, Printer, Snowflake, ShieldAlert, Undo2, FileSignature, ClipboardCheck, FileEdit, Flag, Check as CheckIcon, X as XIcon } from "lucide-react";
import OrderDetailDrawer from "@/components/OrderDetailDrawer";
import OrderEditDialog from "@/components/OrderEditDialog";
import PODViewDialog from "@/components/PODViewDialog";
import PODCapture from "@/components/PODCapture";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { ensureDLToken, getDLToken } from "@/lib/dlToken";
import { toast } from "sonner";
import { fmtDateTime, fmtShortDate } from "@/lib/datetime";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function openLabel(orderId) {
  // SEC-003: mint a 5-min download token instead of leaking the 7-day JWT
  try {
    const t = await ensureDLToken();
    if (!t) return;
    const url = `${BACKEND_URL}/api/orders/${orderId}/label.pdf?auth=${encodeURIComponent(t)}`;
    window.open(url, "_blank", "noopener");
  } catch (e) {
    toast.error("Could not open label — please try again.");
  }
}

// --- Status display mapping ---
// pending + no route + received    -> ACCEPTED (light blue)
// pending + no route + not received -> UNASSIGNED (yellow)
// pending + has route               -> ON ROUTE (treated as assigned)
// assigned                          -> ON ROUTE
// in_transit                        -> IN TRANSIT
// delivered                         -> DELIVERED
// failed                            -> FAILED
function statusFor(o) {
  if (o.status === "returned") return "returned";
  if (o.status === "delivered") return "delivered";
  if (o.status === "failed") return "failed";
  if (o.status === "in_transit") return "in_transit";
  if (o.status === "assigned" || o.route_id) return "on_route";
  if (o.received_from_pharmacy_at) return "accepted";
  return "unassigned";
}

const STATUS_META = {
  unassigned: { label: "UNASSIGNED", cls: "bg-[#fef9c3] text-[#854D0E]" },
  accepted:   { label: "ACCEPTED",   cls: "bg-[#cfe5ff] text-[#1f4ea3]" },
  on_route:   { label: "ON ROUTE",   cls: "bg-[#e0f0ff] text-[#1f4ea3]" },
  in_transit: { label: "IN TRANSIT", cls: "bg-[#e0f0ff] text-[#1f4ea3]" },
  delivered:  { label: "DELIVERED",  cls: "bg-[#dcfce7] text-[#166534]" },
  failed:     { label: "FAILED",     cls: "bg-[#fee2e2] text-[#991B1B]" },
  returned:   { label: "RETURNED",   cls: "bg-[#ffedd5] text-[#9A3412]" },
};

const PRIORITY_COLORS = {
  urgent: "bg-destructive/15 text-destructive",
  cold_chain: "bg-[#3a86ff]/15 text-[#1f4ea3]",
  clinic: "bg-[#7c3aed]/15 text-[#6d28d9]",
  business: "bg-amber-100 text-amber-800",
  normal: "bg-secondary text-secondary-foreground",
};

function fmtDate(iso) { return fmtDateTime(iso); }

const DELIVER_LABELS = {
  same_day: "Same Day",
  next_day: "Next Day",
  otc: "OTC",
  pickup: "Pick Up",
};

export default function OrdersTable({
  orders,
  selectable = false,
  selected = new Set(),
  onToggle = () => {},
  onToggleAll = () => {},
  showPharmacy = true,
  showDriverAndRoute = false,
  showCopay = false,
  hideActions = false,
  drivers = null, // when provided + selectable mode, enables per-order Driver Select column (LEGACY — superseded by openRoutes/onAddToRoute on the route builder)
  onAssignDriver = null, // (order, driverId|null) => Promise (LEGACY)
  openRoutes = null, // when provided + selectable mode, enables per-order "Add to a Route" Select column
  onAddToRoute = null, // (order, routeId) => Promise
  onDelete = null,
  onEdit = null,
  onChanged = null,
  emptyMessage = "No orders yet.",
}) {
  const { user: currentUser } = useAuth();
  const canChangeStatus = currentUser?.role === "super_admin" || currentUser?.role === "dispatcher";
  const canEditOrder = canChangeStatus || currentUser?.role === "pharmacy_staff";
  // "Add to a Route" picker — shown ONLY when (a) we're in the dispatcher's
  // From List view (selectable=true), (b) the parent passed at least one open
  // route, and (c) the current user can change order status. Hidden entirely
  // when there are zero open routes — matches the user requirement that this
  // option is only available when there's a Pending/In-progress/In-transit
  // route to add to.
  const showRoutePicker = selectable && Array.isArray(openRoutes) && openRoutes.length > 0 && canChangeStatus && !!onAddToRoute;
  // Legacy driver picker kept for backwards-compat with any other surface that
  // still passes `drivers` + `onAssignDriver`. Hidden when the new route
  // picker is active so we don't render two columns of pickers at once.
  const showDriverPicker = !showRoutePicker && selectable && Array.isArray(drivers) && drivers.length > 0 && canChangeStatus;
  const [detailId, setDetailId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [podId, setPodId] = useState(null);
  const [podCompleteOrder, setPodCompleteOrder] = useState(null); // when set, opens inline POD-capture dialog
  const [podCorrectOrder, setPodCorrectOrder] = useState(null); // super-admin POD overwrite on a delivered order
  const [priceEdit, setPriceEdit] = useState({ id: null, value: "" }); // inline super-admin price editor
  const [returnTarget, setReturnTarget] = useState(null);
  const [returnComments, setReturnComments] = useState("");
  const [returnDate, setReturnDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [returnBusy, setReturnBusy] = useState(false);
  const allSelected = selectable && orders.length > 0 && orders.every((o) => selected.has(o.id));
  const openDetail = (o) => setDetailId(o.id);
  const openEdit = (o) => setEditId(o.id);

  const openReturn = (o) => {
    setReturnTarget(o);
    setReturnComments("");
    setReturnDate(new Date().toISOString().slice(0, 10));
  };

  // ---- Inline price edit (Super Admin) --------------------------------
  // Super Admin can edit price at any time — including AFTER delivery — so
  // billing reconciliations can happen without re-opening the order. All
  // other roles / statuses are read-only.
  const isSuperAdmin = currentUser?.role === "super_admin";
  const canEditPrice = (o) => isSuperAdmin && o.status !== "returned";
  const startPriceEdit = (o) => {
    if (!canEditPrice(o)) return;
    setPriceEdit({ id: o.id, value: typeof o.price === "number" ? String(o.price.toFixed(2)) : "" });
  };
  const cancelPriceEdit = () => setPriceEdit({ id: null, value: "" });
  const commitPriceEdit = async (o) => {
    const raw = (priceEdit.value || "").replace(/[^0-9.]/g, "");
    const num = Number(raw);
    if (!Number.isFinite(num) || num < 0) {
      toast.error("Enter a valid non-negative number");
      return;
    }
    if (typeof o.price === "number" && Math.abs(o.price - num) < 0.005) {
      cancelPriceEdit();
      return;
    }
    try {
      await api.put(`/orders/${o.id}`, { price: Math.round(num * 100) / 100 });
      toast.success(`Price set to $${num.toFixed(2)}`);
      cancelPriceEdit();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmReturn = async () => {
    if (!returnTarget) return;
    if (!returnComments.trim()) {
      toast.error("Comments are required");
      return;
    }
    if (!returnDate) {
      toast.error("Date of return is required");
      return;
    }
    setReturnBusy(true);
    try {
      const fd = new FormData();
      fd.append("comments", returnComments.trim());
      fd.append("return_date", returnDate);
      await api.post(`/orders/${returnTarget.id}/return`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Order returned to pharmacy${returnTarget.order_number ? ` · #${returnTarget.order_number}` : ""}`);
      setReturnTarget(null);
      setReturnComments("");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReturnBusy(false);
    }
  };

  const dense = "px-2.5 py-3";
  const cell = "px-2.5 py-3";
  const headCell = "px-2.5 py-3 font-medium";
  const cellTight = "px-2 py-3";
  const headCellTight = "px-2 py-3 font-medium";

  return (
    <div className="rounded-xl border border-border bg-card/80 overflow-hidden" data-testid="orders-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            {selectable && <col className="w-9" />}
            <col className="w-[72px]" />{/* Created */}
            <col className="w-[72px]" />{/* Accepted */}
            <col className="w-[112px]" />{/* Order # — wide enough for "SRCIXXXXXXX" */}
            {showPharmacy && <col className="w-[92px]" />}
            <col className="w-[150px]" />{/* Patient */}
            <col />{/* Address — flex */}
            <col className="w-[64px]" />{/* Deliver */}
            <col className="w-[64px]" />{/* Priority */}
            {showDriverAndRoute && <col className="w-[92px]" />}{/* Driver */}
            {showDriverAndRoute && <col className="w-[92px]" />}{/* Route */}
            <col className="w-[82px]" />{/* Status */}
            <col className="w-[56px]" />{/* Miles */}
            <col className="w-[64px]" />{/* Price */}
            {showCopay && <col className="w-[68px]" />}{/* Copay */}
            {!hideActions && <col className={canEditOrder ? "w-[176px]" : "w-[100px]"} />}{/* Actions */}
            {showDriverPicker && <col className="w-[150px]" />}{/* Driver picker (legacy) */}
            {showRoutePicker && <col className="w-[200px]" />}{/* Add to a Route picker (rightmost) */}
          </colgroup>
          <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider">
            <tr>
              {selectable && (
                <th className={`pl-3 pr-2 py-3`}>
                  <Checkbox checked={allSelected} onCheckedChange={onToggleAll} data-testid="orders-table-select-all" />
                </th>
              )}
              <th className={headCell}>Created</th>
              <th className={headCell}>Accepted</th>
              <th className={headCell}>Order #</th>
              {showPharmacy && <th className={headCell}>Pharmacy</th>}
              <th className={headCell}>Patient</th>
              <th className={headCell}>Address</th>
              <th className={headCellTight}>Deliver</th>
              <th className={headCellTight}>Priority</th>
              {showDriverAndRoute && <th className={headCellTight}>Driver</th>}
              {showDriverAndRoute && <th className={headCellTight}>Route</th>}
              <th className={headCellTight}>Status</th>
              <th className={headCellTight}>Miles</th>
              <th className={headCellTight}>Price</th>
              {showCopay && <th className={headCellTight}>Copay</th>}
              {!hideActions && <th className="px-3 py-3"></th>}
              {showDriverPicker && <th className={headCellTight}>Driver</th>}
              {showRoutePicker && <th className={headCellTight} data-testid="orders-table-head-add-to-route">Add to a Route</th>}
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const st = statusFor(o);
              const meta = STATUS_META[st];
              return (
                <tr
                  key={o.id}
                  className="border-t border-border hover:bg-secondary/30 transition-colors cursor-pointer"
                  onClick={() => (selectable ? onToggle(o.id) : openDetail(o))}
                  data-testid={`orders-table-row-${o.id}`}
                >
                  {selectable && (
                    <td className="pl-3 pr-2 py-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(o.id)}
                        onCheckedChange={() => onToggle(o.id)}
                        data-testid={`orders-table-check-${o.id}`}
                      />
                    </td>
                  )}
                  <td className={`${cell} text-xs text-muted-foreground font-mono-num whitespace-nowrap`}>{fmtShortDate(o.created_at)}</td>
                  <td className={`${cell} text-xs font-mono-num whitespace-nowrap text-[#1E6FE0]`} title={o.received_from_pharmacy_at ? `Accepted (received from pharmacy) ${o.received_from_pharmacy_at}` : "Not yet accepted"} data-testid={`orders-table-accepted-${o.id}`} onClick={(e) => { if (!o.received_from_pharmacy_at && canChangeStatus) e.stopPropagation(); }}>
                    {o.received_from_pharmacy_at ? (
                      fmtShortDate(o.received_from_pharmacy_at)
                    ) : canChangeStatus && o.status !== "delivered" && o.status !== "failed" && o.status !== "returned" ? (
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          const btn = e.currentTarget;
                          btn.disabled = true;
                          try {
                            await api.post("/orders/intake", { code: o.id });
                            toast.success(`Accepted #${o.order_number || o.id.slice(0, 6)}`);
                            await onChanged?.();
                          } catch (err) {
                            btn.disabled = false;
                            toast.error(formatApiError(err));
                          }
                        }}
                        className="rounded-full border border-[#1E6FE0]/40 bg-[#1E6FE0]/5 hover:bg-[#1E6FE0]/15 text-[#1E6FE0] text-[10px] font-semibold tracking-wider px-2 py-0.5 uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`orders-table-accept-btn-${o.id}`}
                        title="Mark as Accepted (received from pharmacy)"
                      >
                        Accept
                      </button>
                    ) : ""}
                  </td>
                  <td className={`${cell} font-mono-num text-xs whitespace-nowrap overflow-hidden text-ellipsis`} title={o.order_number || ""} data-testid={`orders-table-order-number-${o.id}`}>
                    <span className="inline-flex items-center gap-1.5">
                      {o.restricted_zip && (
                        <Flag
                          className={`h-3.5 w-3.5 shrink-0 ${
                            o.approval_status === "approved" ? "text-emerald-600" :
                            o.approval_status === "rejected" ? "text-slate-500" :
                            "text-red-600 animate-pulse"
                          }`}
                          fill="currentColor"
                          title={
                            o.approval_status === "approved" ? "Restricted ZIP · approved by Super Admin" :
                            o.approval_status === "rejected" ? "Restricted ZIP · rejected by Super Admin" :
                            "Restricted ZIP · awaiting Super Admin approval"
                          }
                          data-testid={`orders-table-restricted-flag-${o.id}`}
                        />
                      )}
                      <span className="truncate">{o.order_number || "—"}</span>
                    </span>
                  </td>
                  {showPharmacy && (
                    <td className={`${cell} text-muted-foreground truncate`} title={o.pharmacy_name || ""}>{o.pharmacy_nickname || o.pharmacy_name || "—"}</td>
                  )}
                  <td className={cell}>
                    {(() => {
                      const itemsTxt = (o.items || []).map((it) => (it?.name || "").toLowerCase()).join(" | ");
                      const refrig = itemsTxt.includes("refrigerated");
                      const ctrl = itemsTxt.includes("controlled");
                      const badges = (refrig || ctrl) ? (
                        <span className="inline-flex items-center gap-1 shrink-0">
                          {refrig && (
                            <span
                              className="inline-flex items-center justify-center h-4 w-4 rounded bg-[#3a86ff] text-white"
                              title="Refrigerated"
                              data-testid={`row-badge-refrigerated-${o.id}`}
                            >
                              <Snowflake className="h-3 w-3" />
                            </span>
                          )}
                          {ctrl && (
                            <span
                              className="inline-flex items-center text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0 rounded bg-destructive text-destructive-foreground"
                              title="Controlled (CII)"
                              data-testid={`row-badge-controlled-${o.id}`}
                            >
                              CII
                            </span>
                          )}
                        </span>
                      ) : null;
                      return (
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="font-medium truncate" title={o.customer_name}>{o.customer_name}</span>
                          {badges}
                        </div>
                      );
                    })()}
                  </td>
                  <td className={`${cell} text-muted-foreground truncate`} title={o.address}>
                    {o.address}
                    {!o.lat && <span className="ml-2 text-[10px] text-destructive">no coords</span>}
                  </td>
                  <td className={`${cellTight} text-xs text-muted-foreground`}>
                    {o.deliver_type ? DELIVER_LABELS[o.deliver_type] || o.deliver_type : "—"}
                  </td>
                  <td className={cellTight}>
                    <span className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full ${PRIORITY_COLORS[o.priority] || PRIORITY_COLORS.normal}`}>
                      {o.priority?.replace("_", " ")}
                    </span>
                  </td>
                  {showDriverAndRoute && (
                    <td className={`${cellTight} text-muted-foreground text-xs truncate`} data-testid={`orders-table-driver-${o.id}`} title={o.driver_name || ""}>
                      {o.driver_name || "—"}
                    </td>
                  )}
                  {showDriverAndRoute && (
                    <td className={`${cellTight} text-muted-foreground text-xs truncate`} data-testid={`orders-table-route-${o.id}`} title={o.route_label || ""}>
                      {o.route_label || "—"}
                    </td>
                  )}
                  <td className={cellTight}>
                    <div className="flex flex-col gap-1 items-start">
                      <span className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${meta.cls}`} data-testid={`orders-table-status-${o.id}`}>
                        {meta.label}
                      </span>
                      {o.is_next_for_delivery && (
                        <span
                          className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-[#234123] text-white animate-pulse whitespace-nowrap"
                          title="Next stop on the active route"
                          data-testid={`orders-table-next-${o.id}`}
                        >
                          ▶ Next stop
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={`${cellTight} font-mono-num text-sm text-muted-foreground`}>
                    {typeof o.miles === "number" ? `${o.miles.toFixed(1)} mi` : "—"}
                  </td>
                  <td
                    className={`${cellTight} font-mono-num text-sm`}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`orders-table-price-${o.id}`}
                  >
                    {priceEdit.id === o.id ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        autoFocus
                        value={priceEdit.value}
                        onChange={(e) => setPriceEdit((p) => ({ ...p, value: e.target.value }))}
                        onBlur={() => commitPriceEdit(o)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); commitPriceEdit(o); }
                          else if (e.key === "Escape") { e.preventDefault(); cancelPriceEdit(); }
                        }}
                        className="w-16 h-7 px-1.5 py-0.5 text-sm border border-primary/50 rounded bg-background focus:outline-none focus:ring-1 focus:ring-primary font-mono-num"
                        data-testid={`orders-table-price-input-${o.id}`}
                      />
                    ) : canEditPrice(o) ? (
                      <button
                        type="button"
                        onClick={() => startPriceEdit(o)}
                        className="hover:bg-primary/10 rounded px-1.5 py-0.5 -mx-1.5 transition-colors text-foreground"
                        title="Click to edit price"
                        data-testid={`orders-table-price-edit-${o.id}`}
                      >
                        {typeof o.price === "number" ? `$${o.price.toFixed(2)}` : <span className="text-muted-foreground">— +</span>}
                      </button>
                    ) : (
                      typeof o.price === "number" ? `$${o.price.toFixed(2)}` : "—"
                    )}
                  </td>
                  {showCopay && (
                    <td className={`${cellTight} font-mono-num text-sm`} data-testid={`orders-table-copay-${o.id}`}>
                      {typeof o.copay === "number" && o.copay > 0 ? (
                        <span
                          className={
                            o.copay_collected
                              ? "inline-flex items-center gap-1 text-[#166534]"
                              : "inline-flex items-center gap-1 text-[#9A3412]"
                          }
                          title={o.copay_collected ? "Collected (cash)" : "Not yet collected"}
                        >
                          ${o.copay.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  )}
                  {!hideActions && (
                    <td className="px-1 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {isSuperAdmin && o.restricted_zip && o.approval_status === "pending" && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={async () => {
                              try {
                                await api.post(`/orders/${o.id}/restricted-approve`);
                                toast.success(`Order approved${o.order_number ? ` · #${o.order_number}` : ""}`);
                                onChanged?.();
                              } catch (e) { toast.error(formatApiError(e)); }
                            }}
                            className="text-emerald-600 hover:bg-emerald-100 h-7 w-7"
                            data-testid={`orders-table-restricted-approve-${o.id}`}
                            title="Approve restricted-ZIP order"
                          >
                            <CheckIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={async () => {
                              if (!confirm(`Reject and DELETE order${o.order_number ? ` #${o.order_number}` : ""}? This removes the order from the system.`)) return;
                              try {
                                await api.post(`/orders/${o.id}/restricted-reject`);
                                toast.success(`Order rejected & deleted${o.order_number ? ` · #${o.order_number}` : ""}`);
                                onChanged?.();
                              } catch (e) { toast.error(formatApiError(e)); }
                            }}
                            className="text-red-600 hover:bg-red-100 h-7 w-7"
                            data-testid={`orders-table-restricted-reject-${o.id}`}
                            title="Reject & delete restricted-ZIP order"
                          >
                            <XIcon className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDetail(o)}
                        className="text-muted-foreground hover:text-primary h-7 w-7"
                        data-testid={`orders-table-view-${o.id}`}
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openLabel(o.id)}
                        className="text-muted-foreground hover:text-primary h-7 w-7"
                        data-testid={`orders-table-label-${o.id}`}
                        title="Print 4x6 label"
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                      {o.status === "delivered" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setPodId(o.id)}
                          className="text-muted-foreground hover:text-primary h-7 w-7"
                          data-testid={`orders-table-pod-${o.id}`}
                          title="View Proof of Delivery"
                        >
                          <FileSignature className="h-4 w-4" />
                        </Button>
                      )}
                      {(o.status === "delivered" || o.status === "failed") && currentUser?.role === "super_admin" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setPodCorrectOrder(o)}
                          className="text-muted-foreground hover:text-amber-600 h-7 w-7"
                          data-testid={`orders-table-correct-pod-${o.id}`}
                          title="Correct POD (edit photos/signature/comment or switch Delivered ↔ Failed)"
                        >
                          <FileEdit className="h-4 w-4" />
                        </Button>
                      )}
                      {canChangeStatus && o.status !== "delivered" && o.status !== "failed" && o.status !== "returned" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setPodCompleteOrder(o)}
                          className="text-muted-foreground hover:text-primary h-7 w-7"
                          data-testid={`orders-table-complete-pod-${o.id}`}
                          title="Complete delivery (POD) on driver's behalf"
                        >
                          <ClipboardCheck className="h-4 w-4" />
                        </Button>
                      )}
                      {canEditOrder && (
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={
                            o.status === "returned" ||
                            // Delivered orders: only Super Admin gets edit (billing fields only, enforced in dialog).
                            (o.status === "delivered" && !isSuperAdmin) ||
                            (currentUser?.role === "pharmacy_staff" && (o.status === "in_transit" || o.status === "assigned" || !!o.route_id))
                          }
                          onClick={() => openEdit(o)}
                          className="text-muted-foreground hover:text-primary disabled:opacity-30 h-7 w-7"
                          data-testid={`orders-table-edit-${o.id}`}
                          title={
                            currentUser?.role === "pharmacy_staff" && (o.status === "in_transit" || o.status === "assigned" || !!o.route_id)
                              ? "Order is on a route — pharmacy staff can no longer edit"
                              : o.status === "delivered"
                              ? "Edit deliver type / price / copay (delivered)"
                              : "Edit order"
                          }
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {canChangeStatus && (
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={o.status === "delivered" || o.status === "returned"}
                          onClick={() => openReturn(o)}
                          className="text-muted-foreground hover:text-[#7C3AED] disabled:opacity-30 h-7 w-7"
                          data-testid={`orders-table-return-${o.id}`}
                          title="Return to pharmacy"
                        >
                          <Undo2 className="h-4 w-4" />
                        </Button>
                      )}
                      {canEditOrder && onDelete && o.status !== "delivered" && o.status !== "returned" && !(
                        currentUser?.role === "pharmacy_staff" && (o.status === "in_transit" || o.status === "assigned" || o.route_id)
                      ) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(o)}
                          className="text-muted-foreground hover:text-destructive h-7 w-7"
                          data-testid={`orders-table-delete-${o.id}`}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  )}
                  {showDriverPicker && (
                    <td className={cellTight} onClick={(e) => e.stopPropagation()} data-testid={`orders-table-driver-picker-${o.id}`}>
                      <Select
                        value={o.driver_id || "__none__"}
                        onValueChange={async (v) => {
                          try {
                            await onAssignDriver?.(o, v === "__none__" ? null : v);
                          } catch (e) {
                            toast.error(formatApiError(e));
                          }
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs" data-testid={`orders-table-driver-select-${o.id}`}>
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— None —</SelectItem>
                          {drivers.map((d) => (
                            <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  )}
                  {showRoutePicker && (
                    <td className={cellTight} onClick={(e) => e.stopPropagation()} data-testid={`orders-table-route-picker-${o.id}`}>
                      <Select
                        value="__pick__"
                        onValueChange={async (routeId) => {
                          if (routeId === "__pick__") return;
                          try {
                            await onAddToRoute?.(o, routeId);
                          } catch (e) {
                            toast.error(formatApiError(e));
                          }
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs" data-testid={`orders-table-route-select-${o.id}`}>
                          <SelectValue placeholder="Add to a Route…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__pick__" disabled>Add to a Route…</SelectItem>
                          {openRoutes.map((r) => (
                            <SelectItem key={r.id} value={r.id} data-testid={`orders-table-route-option-${r.id}`}>
                              {r.label || `#${(r.id || "").slice(0, 6)}`}
                              {r.driver_name ? ` · ${r.driver_name}` : ""}
                              {r.status ? ` · ${r.status.replace("_", " ")}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  )}
                </tr>
              );
            })}
            {orders.length === 0 && (
              <tr>
                <td colSpan={9 + (selectable ? 1 : 0) + (showPharmacy ? 1 : 0) + (showDriverPicker ? 1 : 0) + (showRoutePicker ? 1 : 0) + (showDriverAndRoute ? 2 : 0) + (showCopay ? 1 : 0) + (hideActions ? 0 : 1)} className="px-5 py-12 text-center text-muted-foreground text-sm">
                  {emptyMessage}
                </td>
              </tr>
            )}
            {/* Totals footer removed per operator request — financial summary
                (Total miles / Total $ / Total copay) lives on Reports and
                Invoices, not in the per-list view. */}
          </tbody>
        </table>
      </div>
      <OrderDetailDrawer orderId={detailId} open={!!detailId} onClose={() => setDetailId(null)} />
      <OrderEditDialog orderId={editId} open={!!editId} onClose={() => setEditId(null)} onUpdated={() => onChanged?.()} />
      <PODViewDialog orderId={podId} open={!!podId} onClose={() => setPodId(null)} />
      {/* Inline POD-capture sheet for super_admin / dispatcher to complete a delivery on a driver's behalf */}
      <Sheet open={!!podCompleteOrder} onOpenChange={(o) => { if (!o) setPodCompleteOrder(null); }}>
        <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto" data-testid="orders-table-pod-sheet">
          <SheetHeader>
            <SheetTitle>Complete delivery (on driver's behalf)</SheetTitle>
            {podCompleteOrder && (
              <p className="text-xs text-muted-foreground mt-1">
                {podCompleteOrder.customer_name}
                {podCompleteOrder.order_number ? <> · #{podCompleteOrder.order_number}</> : null}
                {" — "}{podCompleteOrder.address}
              </p>
            )}
          </SheetHeader>
          {podCompleteOrder && (
            <div className="mt-4">
              <PODCapture
                order={podCompleteOrder}
                allowStatusPicker
                onCompleted={() => {
                  setPodCompleteOrder(null);
                  onChanged?.();
                }}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Super-admin POD correction on an already-delivered order */}
      <Sheet open={!!podCorrectOrder} onOpenChange={(o) => { if (!o) setPodCorrectOrder(null); }}>
        <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto" data-testid="orders-table-pod-correct-sheet">
          <SheetHeader>
            <SheetTitle>Correct Proof of Delivery</SheetTitle>
            {podCorrectOrder && (
              <p className="text-xs text-muted-foreground mt-1">
                {podCorrectOrder.customer_name}
                {podCorrectOrder.order_number ? <> · #{podCorrectOrder.order_number}</> : null}
                {" — "}{podCorrectOrder.address}
              </p>
            )}
          </SheetHeader>
          {podCorrectOrder && (
            <div className="mt-4">
              <PODCapture
                mode="correct"
                order={podCorrectOrder}
                onCompleted={() => {
                  setPodCorrectOrder(null);
                  onChanged?.();
                }}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
      <Dialog open={!!returnTarget} onOpenChange={(o) => { if (!o) { setReturnTarget(null); setReturnComments(""); } }}>
        <DialogContent data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle>Return order to pharmacy</DialogTitle>
          </DialogHeader>
          {returnTarget && (
            <div className="space-y-3 text-sm">
              <p>
                Returning <span className="font-medium">{returnTarget.customer_name}</span>
                {returnTarget.order_number ? <span className="text-muted-foreground"> · #{returnTarget.order_number}</span> : null}
                {" "}back to the pharmacy. This removes the order from its current route and marks it as <span className="font-semibold text-[#7C3AED]">RETURNED</span>.
              </p>
              <div>
                <Label>Date of return <span className="text-destructive">*</span></Label>
                <Input
                  type="date"
                  value={returnDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setReturnDate(e.target.value)}
                  data-testid="return-date"
                />
              </div>
              <div>
                <Label>Comments <span className="text-destructive">*</span></Label>
                <Textarea
                  required
                  value={returnComments}
                  onChange={(e) => setReturnComments(e.target.value)}
                  placeholder="Recipient refused, address inaccessible, package damaged…"
                  data-testid="return-comments"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">These details are saved to the order history.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setReturnTarget(null); setReturnComments(""); }}>Cancel</Button>
            <Button
              onClick={confirmReturn}
              disabled={returnBusy || !returnComments.trim() || !returnDate}
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
              data-testid="return-confirm"
            >
              {returnBusy ? "Returning…" : "Return to pharmacy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
